import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { Link, generatePath } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, Contract_Status_Enum, Photo, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { RoutePaths } from '../../routes/AppRoutes';
import useCurrentBreakpoint from '../../hooks/useCurrentBreakpoint';
import { PackagingFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import SizedImage from '../../common/components/SizedImage';
import FileUtils from '../../common/utils/file.utils';
import { MinimumOrderValue } from '../../common/state/state';
import ContractStatusTag from '../../search/ContractStatusTag';
import { currentContextAtom } from '../../state';
import { currentSubFamilyAtom, companyImageSizeByBreakpoint } from '../../discovery/state';
import { getDeliveryZoneFromAdministrativeArea, getMinimumOrderValueFromDeliveryZones, CompanyDeliveryZone } from '../../common/utils/common.utils';
import { ellipsisStyle } from '../../common/utils/style.utils';
import SocialTags from '../../social-proof/SocialTags';
import InfoSocialTags from '../../social-proof/InfoSocialTags';
import WishlistSupplierButton from '../../wishlist/WishlistSupplierButton';
import { SocialTag } from '../../common/utils/social-tags.utils';

export interface SupplierCardData {
  id: string;
  tradeName: string;
  businessProfile: Business_Profile_Enum | string;
  photo: Pick<Photo, 'domain' | 'path'> | null;
  productFamilies: Product_Sub_Family_Name_Enum[];
  isWholesaler: boolean;
  socialTags: SocialTag[];
  address?: {
    locality?: string | null;
    postalCode?: string | null;
    countryCode: string;
    latitude: number;
    longitude: number;
    formattedAddress: string;
  };
  deliveryZones: CompanyDeliveryZone[];
  partners: string[];
}

interface SupplierCardProps {
  supplier: SupplierCardData;
  displayWishlistButton?: boolean;
  onSupplierClick?: (supplier: SupplierCardData) => void;
  actionButton?: React.ReactNode;
}

export default function SupplierCard({
  supplier,
  displayWishlistButton = false,
  onSupplierClick,
  actionButton,
}: SupplierCardProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'discovery', 'product-family']);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const breakpoint = useCurrentBreakpoint();
  const { companyId, companyAddress } = useAtomValue(currentContextAtom);
  const currentSubFamily = useAtomValue(currentSubFamilyAtom);

  const supplierUrl = generatePath(RoutePaths.DISCOVERY_SUPPLIER, { companyId: supplier.id });

  const contractStatusBadge = useMemo(() => {
    const signedPartner = supplier.partners.find((partner) => partner.includes(`${companyId}_${Contract_Status_Enum.Signed}`));

    if (!signedPartner) {
      return undefined;
    }

    return (
      <ContractStatusTag status={Contract_Status_Enum.Signed} />
    );
  }, []);

  const minimumOrderValue: MinimumOrderValue | undefined = useMemo(() => {
    return getMinimumOrderValueFromDeliveryZones(
      supplier.deliveryZones,
      companyAddress.administrativeAreaLevel2 || '',
    )
  }, [supplier.deliveryZones, companyAddress.administrativeAreaLevel2]);

  const deliveryTime = useMemo((): number | undefined => {
    const deliveryZone = getDeliveryZoneFromAdministrativeArea(
      supplier.deliveryZones,
      companyAddress.administrativeAreaLevel2 || '',
    );

    return deliveryZone?.delivery_time || undefined;
  }, [supplier.deliveryZones, companyAddress.administrativeAreaLevel2]);

  const supplierImage = (
    <SizedImage
      src={FileUtils.BuildCompanyImageUrl({ photo: supplier.photo, type: supplier.businessProfile, size: companyImageSizeByBreakpoint[breakpoint] })}
      alt={t('common:company')}
      width={companyImageSizeByBreakpoint[breakpoint].width!}
      height={{
        xs: companyImageSizeByBreakpoint[breakpoint].height!,
        sm: 'unset',
      }}
      sx={{
        aspectRatio: '5/4',
        flexShrink: 0,
        borderRadius: 0,
        '& .img': {
          transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeOut,
          }),
        },
        '&:hover .img': {
          transform: 'scale(1.08)',
        },
      }}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {
          xs: 'row-reverse',
          sm: 'column',
        },
        backgroundColor: 'surfaceContainerLowest.main',
        borderRadius: {
          xs: 3,
          sm: 4,
        },
        position: 'relative',
        boxShadow: '0 4px 15px 0 rgba(0, 0, 0, 0.03);',
        overflow: 'hidden',
        height: '100%',
        transition: theme.transitions.create('box-shadow', {
          duration: theme.transitions.duration.standard,
          easing: theme.transitions.easing.easeOut,
        }),
        '&:hover': {
          boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {!isXs && !!supplier.socialTags.length && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            zIndex: 6,
          }}
        >
          <SocialTags tags={supplier.socialTags} displayRules={{ rules: { 1: 1, 2: 1 } }} />
        </Box>
      )}
      <Box sx={{ position: 'relative' }}>
        {onSupplierClick ? (
          <Box onClick={() => onSupplierClick(supplier)} sx={{ display: 'block', cursor: 'pointer' }}>
            {supplierImage}
          </Box>
        ) : (
          <Box component={Link} to={supplierUrl} state={{ origin: 'DISCOVERY' }} sx={{ display: 'block' }}>
            {supplierImage}
          </Box>
        )}
        {displayWishlistButton && (
          <Box sx={{ position: 'absolute', bottom: 12, right: 12, zIndex: 5 }}>
            <WishlistSupplierButton supplierId={supplier.id} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          pt: {
            xs: 1,
            sm: 2,
          },
          pl: {
            xs: 1,
            sm: 2,
          },
          pb: {
            xs: 1.5,
            sm: 2,
          },
          pr: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 1,
          minWidth: 0,
        }}
      >
        {isXs && !!supplier.socialTags.length && (
          <Box sx={{ display: 'flex', gap: 0.5, position: 'relative', zIndex: 5 }}>
            <SocialTags tags={supplier.socialTags} displayRules={{ rules: { 1: 1, 2: 1 } }} />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            ml: {
              xs: 0.5,
              sm: 0,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {contractStatusBadge && (
              <Box sx={{ position: 'relative', zIndex: 5 }}>
                {contractStatusBadge}
              </Box>
            )}
            <Tooltip title={supplier.tradeName} enterDelay={500} enterNextDelay={500}>
              <Typography
                variant="titleSmall"
                sx={{
                  ml: contractStatusBadge ? 0.5 : 0,
                  ...ellipsisStyle,
                }}
              >
                {supplier.tradeName}
              </Typography>
            </Tooltip>
          </Box>

          {currentSubFamily === 'ALL' && supplier.productFamilies.length > 0 && (
            <Typography variant="bodySmall" color="secondary" sx={{ position: 'relative', zIndex: 3 }}>
              {t(`product-family:${PackagingFamilyTypeIntl[supplier.productFamilies[0]]}`)}
              {supplier.productFamilies.length > 1 && ` (+${supplier.productFamilies.length - 1})`}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', position: 'relative', zIndex: 3 }}>
          <InfoSocialTags
            minimumOrderValue={minimumOrderValue}
            isWholesaler={supplier.isWholesaler}
            targetGeoLoc={{
              lat: supplier.address!.latitude,
              lng: supplier.address!.longitude,
            }}
            deliveryTime={deliveryTime}
          />
        </Box>
        {actionButton && (
          <Box sx={{ width: '100%', pt: 1, pb: 0.5 }}>
            {actionButton}
          </Box>
        )}
      </Box>
    </Box>
  );
}
